"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var query_string_1 = require("query-string");
var shallowEquals = require("shallow-equal/objects");
function areParamsEquals(paramA, paramB) {
    if (paramA && paramB) {
        return shallowEquals(paramA, paramB);
    }
    return !!paramA === !!paramB;
}
function areBodyEquals(bodyA, bodyB) {
    if (typeof bodyA === 'object' && typeof bodyB === 'object') {
        return shallowEquals(bodyA, bodyB);
    }
    return bodyA === bodyB;
}
function areRequestParamsEquals(paramsA, paramsB) {
    if (paramsA === paramsB) {
        return true;
    }
    return (paramsA.url === paramsB.url &&
        paramsA.method === paramsB.method &&
        areParamsEquals(paramsA.query, paramsB.query) &&
        areBodyEquals(paramsA.body, paramsB.body) &&
        areParamsEquals(paramsA.headers, paramsB.headers));
}
exports.areRequestParamsEquals = areRequestParamsEquals;
function isRequestOk(_a) {
    var status = _a.status;
    return status >= 200 && status <= 299;
}
function formatBody(body) {
    if (body && body.toString() === '[object Object]') {
        // handle case { [key: string]: any } which is not supported by xhr.send
        return JSON.stringify(body);
    }
    return body;
}
function setHeaders(headers, xhr) {
    if (headers) {
        Object.keys(headers).forEach(function (key) { return xhr.setRequestHeader(key, headers[key]); });
    }
}
function createRequestState() {
    return {
        status: 'NOT_SEND',
        statusCode: 0,
        progress: {
            loaded: 0,
            total: 1,
        },
        withLoader: false,
    };
}
var XhrRequest = /** @class */ (function () {
    function XhrRequest(fetchParams, requestOptions) {
        // each success callbacks is called only once (usefull to call after component unmount)
        this.onSuccessCallbacks = [];
        // each error callbacks is called only once (usefull to call after component unmount)
        this.onErrorCallbacks = [];
        this.onStateChangeListeners = [];
        this.state = createRequestState();
        this.xhr = new XMLHttpRequest();
        this.params = fetchParams;
        this.options = requestOptions;
        this.refetch = this.refetch.bind(this);
    }
    XhrRequest.prototype.fetch = function () {
        var _this = this;
        var _a = this.params, url = _a.url, _b = _a.method, method = _b === void 0 ? 'GET' : _b, headers = _a.headers, query = _a.query, body = _a.body, _c = _a.responseType, responseType = _c === void 0 ? 'json' : _c;
        var _d = this.options, _e = _d.loaderDelay, loaderDelay = _e === void 0 ? 0 : _e, _f = _d.withProgress, withProgress = _f === void 0 ? false : _f;
        // if there isn't loader delay, we update request state once
        if (!loaderDelay) {
            this.setRequestState({ status: 'LOADING', withLoader: true });
        }
        else {
            this.setRequestState({ status: 'LOADING' });
            setTimeout(function () {
                if (_this.state.status === 'LOADING') {
                    _this.setRequestState({ withLoader: true });
                }
            }, loaderDelay);
        }
        var urlToCall = query ? url + "?" + query_string_1.stringify(query) : url;
        this.xhr.responseType = responseType;
        this.xhr.onload = function () {
            if (isRequestOk(_this.xhr)) {
                _this.handleSuccess(_this.xhr.response, _this.xhr.status);
            }
            else {
                _this.handleError(_this.xhr.response, _this.xhr.status);
            }
        };
        this.xhr.onerror = function () {
            _this.handleError(_this.xhr.statusText, _this.xhr.status);
        };
        if (withProgress) {
            this.xhr.onprogress = function (_a) {
                var loaded = _a.loaded, total = _a.total;
                _this.handleProgress(loaded, total);
            };
        }
        this.xhr.open(method, urlToCall);
        setHeaders(headers, this.xhr);
        this.xhr.send(formatBody(body));
    };
    XhrRequest.prototype.refetch = function () {
        this.xhr = new XMLHttpRequest();
        this.fetch();
    };
    XhrRequest.prototype.setRequestState = function (newState) {
        var _this = this;
        this.state = tslib_1.__assign({}, this.state, newState);
        this.onStateChangeListeners.forEach(function (onStateChange) { return onStateChange(_this.state); });
    };
    XhrRequest.prototype.handleProgress = function (loaded, total) {
        var progress = { loaded: loaded, total: total };
        this.setRequestState({ progress: progress });
    };
    XhrRequest.prototype.handleSuccess = function (response, statusCode) {
        while (this.onSuccessCallbacks.length > 0) {
            var successCallback = this.onSuccessCallbacks.shift();
            successCallback(response, this.params);
        }
        this.setRequestState({
            status: 'SUCCESS',
            statusCode: statusCode,
            data: response,
            headers: this.getResponseHeaders(),
            withLoader: false,
            error: undefined,
        });
    };
    XhrRequest.prototype.handleError = function (response, statusCode) {
        while (this.onErrorCallbacks.length > 0) {
            var errorCallback = this.onErrorCallbacks.shift();
            errorCallback(response, statusCode, this.params);
        }
        this.setRequestState({ status: 'FAILED', statusCode: statusCode, error: response, withLoader: false });
    };
    XhrRequest.prototype.getResponseHeaders = function () {
        var headers = this.xhr.getAllResponseHeaders();
        var lines = headers.trim().split(/[\r\n]+/);
        var headerMap = {};
        lines.forEach(function (line) {
            var parts = line.split(': ');
            var key = parts.shift();
            var value = parts.join(': ');
            if (key) {
                headerMap[key] = value;
            }
        });
        return headerMap;
    };
    XhrRequest.prototype.hasSameParams = function (params) {
        return areRequestParamsEquals(this.params, params);
    };
    XhrRequest.prototype.addSuccessCallbacks = function (callback) {
        this.onSuccessCallbacks.push(callback);
    };
    XhrRequest.prototype.addErrorCallbacks = function (callback) {
        this.onErrorCallbacks.push(callback);
    };
    XhrRequest.prototype.addStateListener = function (callback) {
        this.onStateChangeListeners.push(callback);
    };
    XhrRequest.prototype.removeStateListener = function (callback) {
        var _a = this.options.abortOnUnmount, abortOnUnmount = _a === void 0 ? true : _a;
        var listenerIndex = this.onStateChangeListeners.indexOf(callback);
        if (listenerIndex >= 0) {
            this.onStateChangeListeners.splice(listenerIndex, 1);
            if (abortOnUnmount &&
                this.onStateChangeListeners.length === 0 &&
                this.state.status === 'LOADING') {
                this.xhr.abort();
                this.setRequestState({ status: 'NOT_SEND', withLoader: false });
                if (this.onAbort) {
                    this.onAbort();
                }
            }
        }
        else {
            console.warn('loti-request : Error in removeStateListener `callback` not found');
        }
    };
    XhrRequest.prototype.updateOptions = function (options) {
        // in case request is get from cache for a new component which has `abortOnUnmount` disabled
        if (this.options.abortOnUnmount && !options.abortOnUnmount) {
            this.options.abortOnUnmount = options.abortOnUnmount;
        }
    };
    return XhrRequest;
}());
exports.default = XhrRequest;
